import { AlertCircle, Ban, Inbox, LoaderCircle, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

type ContentStateKind = "loading" | "empty" | "error" | "forbidden";

const stateDefaults: Record<ContentStateKind, { icon: LucideIcon; title: string }> = {
  loading: { icon: LoaderCircle, title: "Carregando" },
  empty: { icon: Inbox, title: "Nenhum item por aqui" },
  error: { icon: AlertCircle, title: "Não foi possível carregar" },
  forbidden: { icon: Ban, title: "Acesso não disponível" },
};

type ContentStateProps = {
  kind: ContentStateKind;
  title?: string;
  description: string;
  action?: ReactNode;
  compact?: boolean;
  className?: string;
};

export function ContentState({ kind, title, description, action, compact = false, className }: ContentStateProps) {
  const defaults = stateDefaults[kind];
  const Icon = defaults.icon;
  const isLive = kind === "loading" || kind === "error";

  return (
    <section
      role={kind === "error" ? "alert" : undefined}
      aria-live={isLive ? "polite" : undefined}
      aria-busy={kind === "loading" ? true : undefined}
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border border-dashed bg-card px-5 text-center shadow-(--shadow-surface)",
        compact ? "min-h-40 py-6" : "min-h-64 py-10",
        className,
      )}
    >
      <span className={cn("mb-4 grid size-13 place-items-center rounded-lg bg-(--surface-warning) text-primary-strong", kind === "error" && "bg-(--surface-danger) text-danger", kind === "forbidden" && "bg-muted text-muted-foreground")}>
        <Icon aria-hidden="true" className={cn("size-5", kind === "loading" && "animate-spin motion-reduce:animate-none")} />
      </span>
      <h2 className="text-base font-semibold text-foreground">{title ?? defaults.title}</h2>
      <p className="mt-1 max-w-md text-sm leading-6 text-muted-foreground">{description}</p>
      {action ? <div className="mt-5 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </section>
  );
}
